import React, { useState, useEffect, useRef, useCallback } from "react";
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { 
  Button, 
  Divider,
  IconButton, 
  Tooltip, 
  Toolbar, 
  Typography, 
  CircularProgress, 
  Paper, 
  Card, 
  CardContent, 
  CardMedia,
  CardActionArea,
  Table, 
  TableRow, 
  TableBody, 
  TableCell, 
  TableContainer, 
  Grid, 
  Tabs,
  Tab,
  Fade,
  FormControl, 
  FormControlLabel, 
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Box} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TranslateOutlined from '@mui/icons-material/TranslateOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

import OfflineBoltRoundedIcon from '@mui/icons-material/OfflineBoltRounded';
import StarsRoundedIcon from '@mui/icons-material/StarsRounded';
import OfflinePinRoundedIcon from '@mui/icons-material/OfflinePinRounded';
import CrownIcon from './icons/CrownIcon';
import upperFirst from 'lodash/upperFirst';
import { glintOnHover } from '../theme/glint';
import classNames from 'classnames';

import IOSSwitch from "./IOSSwitch";
import { ManuallyAddLangifyCode } from './ThemeAppExtension.js';
import { getPlans, changePlan } from "../modules/plans";
import LoadingIndicator from './LoadingIndicator';
import API from '../api/api';
import { Trans, useTranslation } from 'react-i18next';
import { PieChart, Pie } from 'recharts';
import { formatLargeNumber } from '../helper.js';




const _plans = ['free', 'basic', 'growth', 'premium'];
const PLAN_YELLOW = '#ffaf23';
const PLAN_YELLOW_TEXT = '#3a20cd';

// Reports a plan change click to Google Analytics (gtag is defined in index.html)
const trackPlanChange = (eventName, oldPlanName, newPlanName) => {
  if (typeof window.gtag !== 'function') return;
  window.gtag('event', eventName, {
    // the shop's charge name and the plan keys may differ in case, as in handleChangePlan
    old_plan_name: oldPlanName?.toLowerCase(),
    new_plan_name: newPlanName?.toLowerCase(),
  });
};




const styles = {
  h4: {
    color: '#000000',
  },
  customTooltip: (theme) => ({
    backgroundColor: '#ffaf23',
    borderColor: '#ffaf23',
    color: 'white',
    border: '1px solid black',
    padding: theme.spacing(1),
    fontSize: '1rem',
    '& *': {
      textShadow: '0 0 2px rgba(0,0,0,0.5)',
    }
  }),
  tooltip: (theme) => ({
    backgroundColor: PLAN_YELLOW,
    color: PLAN_YELLOW_TEXT,
    boxShadow: theme.shadows[5],
  }),
  tooltipButton: {
    color: PLAN_YELLOW_TEXT,
    borderColor: PLAN_YELLOW_TEXT,
    marginTop: '8px',
  },
  arrow: {
    fontSize: '24px',
    color: '#ffaf23',
  },
  toolbar: {
    marginBottom: '24px',
    backgroundColor: '#cecece',
    color: 'initial',
    cursor: 'pointer',
    opacity: .75,
    borderRadius: '4px',
    transition: 'opacity .5s ease, background-color .5s ease, color .5s ease, filter .5s ease',
    filter: 'brightness(1)',
    '& h3, & .MuiButton-root': {
      color: '#000000',
      fontWeight: '400 !important',
      borderColor: '#000000'
    },
    // dimmed, so the yellow upgrade hint stands out against it
    '&:hover, &.hover': {
      backgroundColor: PLAN_YELLOW,
      color: '#fff',
      opacity: 1,
      filter: 'brightness(0.5)'
    },
    '&:hover h3, &.hover h3, &.hover .MuiButton-root': {
      color: 'white',
      borderColor: '#ffffff'
      //textShadow: '1px 1px 3px rgba(0, 0, 0, 0.5)'
    },
  },
  updatePlanBtn: {
    position: 'absolute',
    right: '16px',
  },
  iconWrapper: {
    position: 'relative',
    marginRight: '12px',
    fontSize: '2rem'
  },
  icon: {
    marginRight: '16px',
    //opacity: .5,
    cursor: 'pointer',
    verticalAlign: 'middle',
  },
  iconSVG: {
    color: '#ffaf23',
    fontSize: '2rem',
  },
  startPos: {
    position: 'absolute',
    zIndex: 100,
    left: 0,
  },
  endPos: {
    position: 'absolute',
    zIndex: 100,
    right: '8px',
    left: 'auto',
  },
  overlayed: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    justifyContent: 'flex-end',
    zIndex: 1
  },
  chartContainer: {
    marginLeft: '-100px',
  },

  radarChart: {
    padding: '10px 0',
    '& text': {
      fontSize: '.8rem',
    },
    '& .recharts-surface': {
      height: '230px !important',
      overflow: 'initial'
    }
  },


  autoTransText: {
    color: '#999 !important',
    fontSize: '1.75rem',
    fontWeight: '400 !important',
  },


  media: {
    width: '100%',
    height: 200,
    backgroundColor: '#fff',
  },

  card: {
    height: '100%',
  },
  cardContent: (theme) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    width: '100%',
    backgroundColor: '#cecece',
    opacity: 0.75,
    position: 'relative',
    padding: '16px !important',
    transition: 'opacity .5s ease, background-color .5s ease, color .5s ease, filter .5s ease',
    filter: 'brightness(1)',
    [theme.breakpoints.down('md')]: {
      minHeight: 0,
    },
    // dimmed, so the yellow upgrade hint stands out against it
    '&:hover, &.hover': {
      backgroundColor: PLAN_YELLOW,
      color: '#fff',
      opacity: 1,
      filter: 'brightness(0.5)',
      '& *': {
        color: '#ffffff !important'
      }
    },
  }),
  statistics: {
    padding: '16px 0 0 0 !important',
  },
  fluidContent: {
    padding: '0 !important',
  },
  cardHeadline: {
    margin: '.5rem 0',
    fontWeight: 400,
    color: '#000 !important', // '#1a237e !important',
  },
  cardHeadline2: {
    fontSize: '1.8rem',
    fontWeight: 400,
    color: '#000 !important',
  },
  cardBody: {
    margin: '20px 0 0 0',
    color: '#999'
  },
  cardBtn: {
    marginTop: '.5rem 0',
    color: '#fff',
  },
  progressNumContent: (theme) => ({
    position: 'absolute',
    top: '50%',
    left: 0,
    transform: 'translateY(-50%)',
    textAlign: 'center',
    width: 300,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  }),
  progressNum: {
    color: '#000000 !important',
    fontWeight: '300 !important',
  },
  progressNumText: {
    color: '#999 !important',
    fontWeight: '400 !important',
  },
};



export const CustomTooltip = ({ title, features, neededPlan, children, isBlock, placement, overlayed, onClose }) => {
  const { t } = useTranslation();  // i18n hook
  const currentPlan = useSelector((state) => state.shop.shop.charge.name);

  let theOffset = [0, 0];
  switch(placement) {
    case 'left': theOffset = [0, -100]; break;
    case 'right': theOffset = [0, 100]; break;
    case 'top': theOffset = [0, -25]; break;
    case 'bottom': theOffset = [0, -25]; break;
    case 'right-middle': theOffset = [0, 50]; break;
  }

  let isDisabled = true;
  const _checkDisabled = () => {
    const neededPlanIndex = _plans.findIndex(x => x === neededPlan);
    let disabledPlans = _plans.slice(neededPlanIndex-1);
    console.log(disabledPlans, disabledPlans.includes(currentPlan))
    return true;
    return disabledPlans.includes(currentPlan);
  }
  
  const handleClose = (e) => {
    if(onClose) onClose(e);
  }
  
  const renderTooltip = () => {
    return <Tooltip
      //disabled={_checkDisabled()}
      title={//_checkDisabled() ? null :
        <div>
          <Typography variant="h6" style={{ color: PLAN_YELLOW_TEXT, display: 'flex', alignItems: 'center' }}>
            <strong style={{ display: 'flex', alignItems: 'center' }}>
              {neededPlan === 'basic' && <OfflineBoltRoundedIcon sx={styles.iconWrapper} />}
              {neededPlan === 'growth' && <StarsRoundedIcon sx={styles.iconWrapper} />}
              {neededPlan === 'premium' && <CrownIcon sx={styles.iconWrapper} />}
              {title}
            </strong>
          </Typography>
          {features &&
            <>
              <Typography variant="body1">
                {neededPlan === 'premium' && t('plans.customTooltip.upgradeToPremium')}
                {neededPlan && neededPlan !== 'premium' && t('plans.customTooltip.upgradeToSpecificPlan', { plan: upperFirst(neededPlan) })}
                {!neededPlan && t('plans.customTooltip.upgradeToNextPlan')}
              </Typography>
              <ul style={{paddingLeft: 16}}>
                {features.map((feature) => {
                  return <Typography component={'li'} key={`featurelist-${feature}`} variant="body1">{feature}</Typography>
                })}
              </ul>
            </>
          }
          {!features &&  
            <Typography variant="body1">{t('plans.customTooltip.updateToNextPlan')}</Typography>
          }
          <Typography>
            <Button sx={[styles.tooltipButton, glintOnHover]} component={Link} to="/profile/plans" variant="outlined" size="small">
              {t('plans.customTooltip.updatePlanButton')}
            </Button>
          </Typography>
        </div>
      }
      slotProps={{
        ...(overlayed ? {
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: theOffset,
                },
              },
            ],
          },
        } : {}),
        tooltip: { sx: styles.tooltip },
        arrow: { sx: styles.arrow },
      }}
      onAbort={handleClose}
      onClose={handleClose}
      arrow
      placement={placement === 'right-middle' ? 'right' : (placement || 'top')}
    >
      {isBlock ?
        <div style={{ display: isBlock ? 'block' : 'inline-block' }}>
          {children}
        </div>
      :
        children
      }
    </Tooltip>
  }

  return <React.Fragment>
    {isBlock ?
      <div style={{ display: isBlock ? 'block' : 'inline-block' }}>
        {renderTooltip()}
      </div>
    :
      renderTooltip()
    }
  </React.Fragment>
};


export const MenuItemDummy = ({}) => {

  useEffect(() => {
      
  }, []);

  return <>

  </>
}


// the hint's tooltip around a whole element, e.g. a locked navigation row: a disabled button gets
// no hover events of its own
export const UpgradePlanHintArea = ({features, placement, neededPlan, children}) => {
  const { t } = useTranslation();

  return <CustomTooltip
    title={t('plans.upgradePlanHint.tooltipTitle')}
    features={features}
    placement={placement}
    neededPlan={neededPlan}
  >
    {children}
  </CustomTooltip>
}

export const UpgradePlanHint = ({features, position, placement, top, neededPlan, isBlock, isOverlay, hideIcons, noTooltip}) => {
  const { t } = useTranslation();

  useEffect(() => {

  }, []);

  const hint = <Box className={'upgrade-plan-hint'}
        sx={{
          ...styles.icon,
          ...(position === 'end' ? styles.endPos : styles.startPos),
          ...(isOverlay ? styles.overlayed : {}),
          ...(!isOverlay ? { top: top || 'auto' } : {}),
          ...(top === '50%' ? { transform: 'translateY(-50%)' } : {}),
          display: 'flex',
          alignItems: 'center',
        }}>

        {!hideIcons ? (
          <React.Fragment>
            <LockOutlinedIcon style={{fontSize: '1.2rem', opacity: .75}} />

            {neededPlan === 'basic' && <OfflineBoltRoundedIcon sx={styles.iconSVG} />}
            {neededPlan === 'growth' && <StarsRoundedIcon sx={styles.iconSVG} />}
            {neededPlan === 'premium' && <CrownIcon sx={styles.iconSVG} />}
          </React.Fragment>
        ) : (
          <div style={{width: '100%', height: '100%', minWidth: '24px', minHeight: '24px', cursor: 'pointer'}}>&nbsp;</div>
        )}
      </Box>;

  if (noTooltip) return hint;

  return <>
    <CustomTooltip
      title={t('plans.upgradePlanHint.tooltipTitle')}
      features={features}
      placement={placement}
      style={{position: 'relative'}}
      neededPlan={neededPlan}
      isBlock={isBlock}
    >
      {hint}
    </CustomTooltip>
  </>
}


export const AutoTranslationsDummy = ({}) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const availableWords = useSelector((state) => state.shop.shop.auto_translations.available_words);
  const totalUsedWords = useSelector((state) => state.shop.shop.auto_translations.total_used_words);
  const wordsLeft = useSelector((state) => state.shop.shop.auto_translations.availableWords);

  const getFontSize = (str) => {
    let res = 2.75;
    if(str.length === 7) res = 2.5;
    else if(str.length === 8) res = 2.25;
    else if(str.length === 9) res = 2;
    else if(str.length > 9) res = 1.75;
    return res;
  }

  return <Grid size={{ xs: 12, md: 8 }}>
    <CustomTooltip
      title={t('plans.autoTranslationsDummy.tooltipTitle', 'Plan upgrade needed')}
      features={[
        t('plans.autoTranslationsDummy.feature1'),
        t('plans.autoTranslationsDummy.feature2')
      ]}
      placement={window.innerWidth < 750 ? 'bottom' : 'left'}
      neededPlan={'basic'}
      isBlock
      overlayed
      onClose={() => setIsHovered(false)}
    >
      <Card id="autoTranslationTile" 
        sx={styles.card} 
        onMouseEnter={() => setIsHovered(true)}
        //onMouseOut={() => setIsHovered(false)}
      >
        <CardContent className={isHovered ? 'hover' : undefined} sx={[styles.cardContent, styles.fluidContent]}>
          <Typography variant="subtitle1" style={{marginLeft: 24, position: 'relative', top: 9, fontSize: '1.17em', color: 'rgba(0, 0, 0, 0.87)', opacity: '0.65', paddingTop: 7, paddingBottom: 7}}>
            {t('autoTranslationsTile.title')}
          </Typography>
          <Divider style={{marginTop: 16, marginBottom: 0}} />  
          <Box sx={{display: 'flex', flexDirection: {xs: 'column', sm: 'row'}, alignItems: 'center', position: 'relative', minHeight: {xs: 'auto', sm: 250}}}>
              <Box sx={{width: {xs: '100%', sm: 300}, minHeight: 250, position: 'relative', flexShrink: 0}}>
                <PieChart width={300} height={250} style={{position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}>
                  <Pie data={[{"name": "Translated Fields", "value": 2140 }]} endAngle={300} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={96} outerRadius={100} fill="#000000" stroke="none" />
                </PieChart>
                <Fade in={true}>
                  <Box sx={styles.progressNumContent}>
                    <Typography variant="h2" sx={styles.progressNum}
                      style={{
                        fontSize: `${getFontSize(formatLargeNumber(availableWords ? availableWords : 10000))}rem`
                      }}>
                      {formatLargeNumber(availableWords ? availableWords : 10000)}</Typography>
                    <Typography variant="body1" sx={styles.progressNumText}>{t('autoTranslationsTile.wordsLeft')}</Typography>
                  </Box>
                </Fade>
              </Box>

            <Box sx={{textAlign: 'center', padding: '16px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, minWidth: 0}}>
          <Typography variant="h5" sx={{...styles.h4, margin: '0 0 12px 0'}}>
            <TranslateOutlined style={{marginRight: 12, fontSize: 35, verticalAlign: 'top' }} />
            {t('autoTranslationsTileDummy.title')}
          </Typography>
          <Divider variant="middle" />
          <Typography variant="subtitle1" style={{marginBottom: 24, color: '#999', fontSize: '1.4rem', fontWeight:400, lineHeight: '1.4rem'}}>
            <small>
              <Trans i18nKey='autoTranslationsTileDummy.usedWordsInfo' values={{availableWords: formatLargeNumber(availableWords ? availableWords : "10000"), totalUsedWords: formatLargeNumber(totalUsedWords ? totalUsedWords : "0") }}>
                {{availableWords}}
                {{totalUsedWords}}
              </Trans>
            </small>
          </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  </CustomTooltip>
</Grid>
}


export const SwitcherConfiguratorDummy = () => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Paper elevation={1}>
      <CustomTooltip 
        title={t('plans.switcherConfiguratorDummy.tooltipTitle', 'Plan upgrade needed')}
        features={[
          t('plans.switcherConfiguratorDummy.feature1', 'Language, country and currency switchers'),
          t('plans.switcherConfiguratorDummy.feature2', 'Different switcher templates'),
          t('plans.switcherConfiguratorDummy.feature3', 'Automatic language detection'),
          t('plans.switcherConfiguratorDummy.feature4', 'Language recommendation dialog')
        ]}
        placement='bottom'
        isBlock
        overlayed
        neededPlan='basic'
        onClose={() => setIsHovered(false)}
      >
        <Toolbar 
          className={isHovered ? 'hover' : undefined} sx={styles.toolbar}
          onMouseEnter={() => setIsHovered(true)}
        >
          <h3>
            <LockOutlinedIcon sx={styles.icon} /> {t('plans.switcherConfiguratorDummy.header', 'Switcher Configurator')}
          </h3>
          <Button 
            component={Link} 
            to="/profile/plans" 
            variant="outlined" 
            sx={[glintOnHover, styles.updatePlanBtn]}
          >
            {t('plans.switcherConfiguratorDummy.updatePlanButton', 'Upgrade plan')}
          </Button>
        </Toolbar>
      </CustomTooltip>
    </Paper>
  );
};


export const ThemeSettingsDummy = () => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const foundCodeInTheme = useSelector((state) => state.themeAppExtension.langify_app_embed_code_in_theme);

  return (
    <Paper elevation={1}>
      <CustomTooltip 
        title={t('plans.themeSettingsDummy.tooltipTitle', 'Plan upgrade needed')}
        features={[
          t('plans.themeSettingsDummy.feature1', 'Custom Content settings'),
          t('plans.themeSettingsDummy.feature2', 'Link-Correction')
        ]}
        placement='bottom'
        isBlock
        overlayed
        neededPlan='basic'
        onClose={() => setIsHovered(false)}
      >
        <Toolbar 
          className={isHovered ? 'hover' : undefined} sx={styles.toolbar}
          onMouseEnter={() => setIsHovered(true)}
        >
          <h3>
            <LockOutlinedIcon sx={styles.icon} /> {t('plans.themeSettingsDummy.header', 'Theme settings')}
          </h3>
          <Button 
            component={Link} 
            to="/profile/plans" 
            variant="outlined" 
            sx={[glintOnHover, styles.updatePlanBtn]}
          >
            {t('plans.themeSettingsDummy.updatePlanButton', 'Upgrade plan')}
          </Button>
        </Toolbar>
      </CustomTooltip>
      <ManuallyAddLangifyCode 
        publish={false}
        showAlways={true}
        showInline={true}
        hideHeadline={true}
        hideDescription={true}
        forceHide={!foundCodeInTheme}
        style={{marginTop: -24, paddingLeft: 8}}
      />
    </Paper>
  );
};





/** ---------------------------------------------------------------------------------------------
 * PLAN SELECT COMPONENT
 * 
 * --------------------------------------------------------------------------------------------- */
const planSelectStyles = {
  root: {
    display: 'block',
    position: 'relative',
  },
  gridItem: (theme) => ({
    position: 'relative',
    margin: theme.spacing(1),
    width: `calc(20% - ${theme.spacing(2)})`,
    minWidth: '200px',
    flexShrink: 0,
    '&.first': {
      marginLeft: '25%',
      borderRadius: '8px',
      '& > .inner': {
        width: '100%',
        height: '100%',
        '& > *': {
          width: '100%',
          height: '100%',
        }
      }
    },
  }),
  card: (theme) => ({
    // the extra classes outrank the dark theme's divider border on elevated papers in main
    '&.current, main &.current.MuiPaper-root.MuiCard-root': {
      border: `solid 4px ${theme.palette.custom.main}`,
      borderRadius: '8px',
      marginTop: '-4px',
      '& .MuiCardMedia-root': {
        /**
         * Die goldene Hervorhebung des aktuellen Plans. Im Dark Mode muss der
         * invert-Teil aus `psStyles.media` hier wiederholt werden: dieser
         * Nachfahren-Selektor hat die höhere Spezifität und ersetzt den
         * `filter` der Media-Klasse sonst vollständig. Die weisse Bildfläche
         * bliebe dann stehen — und würde durch das `mixBlendMode: 'screen'`,
         * das weiter greift, sogar noch aufgehellt.
         *
         * Schwarz übersteht die ganze Kette (hue-rotate lässt den Nullvektor
         * unberührt, 0 × 1.75 = 0), der Hintergrund verschwindet also
         * weiterhin exakt in der Kartenfläche.
         */
        filter: theme.palette.mode === 'dark'
          ? 'invert(1) hue-rotate(180deg) hue-rotate(160deg) brightness(1.75)'
          : 'hue-rotate(160deg) brightness(1.75)',
      }
    },
    '&.best-deal': {
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    },
    '&:hover': {
      '& .showMoreBtn': {
        opacity: 1,
      }
    }
  }),
  cardContent: {
    textAlign: 'center',
    position: 'relative',
    display: 'flex',
    flexDirection:  'column',
  },
  showMoreBtn: {
    opacity: 0,
  },
  showAllFeatures: {
  },
  // the heads get the same height (adjustCardHeadHeights), the button at the bottom keeps them in one row
  cardHead: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
  },
  headButton: {
    marginTop: 'auto',
  },
  /**
   * Die Plan-Icons sind opake RGB-WebPs ohne Alphakanal — das Weiss steckt in
   * den Pixeln und lag im Dark Mode als ausgeschnittene weisse Kachel auf der
   * Karte.
   *
   * `invert(1)` macht daraus Schwarz, und Schwarz ist das neutrale Element von
   * `screen`: der Hintergrund verschwindet exakt in der Kartenfläche. Das Motiv
   * selbst ist mittelhell, weshalb die `hue-rotate(180deg)`-Matrix es nahe an
   * seiner Originalfarbe wieder aufbaut, statt es zu verfremden.
   *
   * `multiply` allein trägt hier NICHT: es würde das pastellfarbene Motiv
   * genauso abdunkeln wie den Hintergrund (1.16:1 zur Karte).
   *
   * Der 1px-Überzoom schneidet die äusserste Pixelreihe weg. `growth.webp` hat
   * dort einen eingebrannten Rand auf 232 statt 255, der auf weissem Grund
   * unsichtbar ist, nach dem Invertieren aber als helle Linie aufschlägt.
   */
  media: (theme) => ({
    width: 150,
    height: 150,
    // only horizontally: in the column of cardHead a vertical auto margin would eat the free space
    margin: '0 auto',
    ...(theme.palette.mode === 'dark' && {
      filter: 'invert(1) hue-rotate(180deg)',
      mixBlendMode: 'screen',
      backgroundSize: 'calc(100% + 2px) calc(100% + 2px)',
      backgroundPosition: 'center',
    }),
  }),
  fixedCard: (theme) => ({
    position: 'absolute',
    left: 0,
    top: '32px',
    bottom: '16px',
    width: `calc(25% - ${theme.spacing(1)})`,
    [theme.breakpoints.up('lg')]: {
      width: 'calc(20% - 16px)',
      left: '8px',
    },
  }),
  cardsContainer: (theme) => ({
    overflow: 'hidden',
    position: 'relative',
    paddingTop: '24px',
    [theme.breakpoints.up('lg')]: {
      overflow: 'hidden !important',
    },
    '& > .innerContainer': {
      display: 'flex',
      flexDirection: 'row',
      padding: '8px 0',
      paddingBottom: '16px'
    },
  }),
  bestDeal: {
    position: 'absolute',
    top: '-32px',
    left: 0,
    width: '100%',
    fontWeight: 500,
    backgroundColor: '#ffab00',
    color: PLAN_YELLOW_TEXT,
    padding: '6px',
    textAlign: 'center',
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
  },
  title: {
    textAlign: 'center',
  },
  pos: {
    marginBottom: '12px',
  },
  button: (theme) => ({
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    '&:hover': {
      color: 'white',
      backgroundColor: theme.palette.custom.main,
      borderColor: theme.palette.custom.main,
    },
    '&.green:hover': {
      backgroundColor: 'green',
      borderColor: 'darkgreen',
    },
    '&.red:hover': {
      backgroundColor: 'red',
      borderColor: 'darkred',
    }
  }),
  tableContainer: {
    marginLeft: '-16px',
    marginRight: '-16px',
    width: 'auto',
    overflow: 'hidden',
  },
  table: {
    marginBottom: '16px',
    '&.featureTable': {
      '& th': {
        textAlign: 'right'
      },
      '& th.featureLabel': {
        textAlign: 'left'
      }
    },
    '& .featurelist': {
      marginTop: '8px'
    }
  },
  checkIcon: {
    margin: '-3px',
    color: 'green'
  },
  noCheckIcon: {
    margin: '-3px',
    color: 'red'
  },
  alternateRow: (theme) => ({
    backgroundColor: theme.palette.action.hover,
  }),

  singleline: {
    whiteSpace: 'nowrap',
  },

  anualSwitch: {
    display: 'flex',
    flexDirection: 'row',
    verticalAlign: 'top',
    justifyContent: 'center',
    alignContent: 'center',
    flexWrap: 'nowrap',
  },
  mobileTabs: (theme) => ({
    background: theme.palette.background.paper,
    borderRadius: '4px',
    '& .MuiTab-root': {
      minWidth: 'auto'
    }
  }),
  // disabled, but meant to be read
  currentPlanButton: (theme) => ({
    '&.Mui-disabled': {
      color: theme.palette.text.secondary,
    },
  }),
  lostFeatureList: {
    listStyle: 'none',
    padding: 0,
    margin: '8px 0 0',
  },
  lostFeature: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    marginBottom: '4px',
  },
  // the plans table's red cross, nudged onto the first text line
  lostFeatureIcon: {
    color: 'red',
    fontSize: '1.25rem',
    flexShrink: 0,
    marginTop: '2px',
  },
  downgradeButton: {
    color: '#fff',
    backgroundColor: 'red',
    borderColor: 'darkred',
    '&:hover': {
      backgroundColor: 'darkred'
    }
  }
};


const StyledRedTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  '& .MuiTooltip-tooltip': {
    backgroundColor: 'red',
    color: 'white',
    fontSize: 14,
  },
  '& .MuiTooltip-arrow': {
    color: 'red',
  },
});

const RedTooltip = ({ title, disabled, children, ...props }) => {
  if (disabled) {
    return children;
  }
  return (
    <StyledRedTooltip title={title} {...props}>
      {children}
    </StyledRedTooltip>
  );
};

const formatPrice = (price) => `$ ${parseFloat(price).toFixed(2)}`;

const formatDate = (date) => {
  const parsed = new Date(date);
  return isNaN(parsed.valueOf()) ? '' : parsed.toLocaleDateString(navigator.language, { year: 'numeric', month: 'long', day: 'numeric' });
};

/**
 * "3 months free trial" for whole 30-day intervals (the staff gives trials in months), else days.
 */
export const trialLabel = (t, days) => (days % 30 === 0
  ? t('plans.planSelect.trialMonths', { count: days / 30 })
  : t('plans.planSelect.trialDays', { count: days }));

export const PlanSelect = () => {
  const psStyles = planSelectStyles;
  const { t } = useTranslation();
  const plans = useSelector((state) => state.plans.plans);
  const featureList = useSelector((state) => state.plans.featureList);
  const shopPlan = useSelector((state) => state.shop.shop.charge.name);
  const pendingDiscount = useSelector((state) => state.plans.discount);
  const currentDiscount = useSelector((state) => state.plans.current_discount);
  const availableWords = useSelector((state) => state.shop.shop.auto_translations?.available_words ?? 0);
  const translationLanguageCount = useSelector((state) => (state.languages.languages || []).filter(x => !x.base).length);
  const dispatch = useDispatch();

  const [loaded, setLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const changePlanPending = useSelector((state) => state.plans.change_plan_pending);
  // a plan change that did not end in Shopify's redirect (error, no confirmation url) releases the overlay
  useEffect(() => {
    if(!changePlanPending && isLoading) setIsLoading(false);
  }, [changePlanPending]);
  const [anualPayment, setAnualPayment] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const [showFullList, setShowFullList] = useState(false);
  const [showDowngradeWarning, setShowDowngradeWarning] = useState(false);
  const [loosingFeatures, setLoosingFeatures] = useState([]);
  const [newPlanId, setNewPlanId] = useState(null);

  const scrollContainer = useRef(null);
  const innerContainer = useRef(null);

  const [glossaryCount, setGlossaryCount] = useState(null);

  // keys of plans.loosingFeatures, per plan that adds them
  const _features = {
    'free': [],
    'basic': [
      'automaticTranslations',
      'thirdPartyAppTranslation',
      'imageTranslations',
      'switcherConfigurator',
      'languageDetectionRecommendation',
      'exportImportFeature',
      'languagesHidden'
    ],
    'growth': [
      'bulkTranslation'
    ],
    'premium': [
      'prioritySupport'
    ]
  };

  // the downgrade dialog names the shop's own numbers where it has them
  const getPlanLimit = (planKey, featureKey) => {
    const plan = plans.find(x => x.key?.toLowerCase() === planKey);
    return Number(plan?.[featureKey]) || 0;
  }

  const describeLostFeature = (featureKey, newPlanKey) => {
    if (featureKey === 'automaticTranslations' && availableWords > 0) {
      return t('plans.loosingFeatures.machineTranslationWords', { words: formatLargeNumber(availableWords) });
    }
    if (featureKey === 'languagesHidden') {
      const limit = getPlanLimit(newPlanKey, 'languages');
      if (translationLanguageCount <= limit) return null;
      return t('plans.loosingFeatures.languagesHidden', { over: translationLanguageCount - limit, total: translationLanguageCount });
    }
    return t(`plans.loosingFeatures.${featureKey}`);
  }

  const describeLostGlossary = (newPlanKey) => {
    const limit = getPlanLimit(newPlanKey, 'glossary_entries');
    if (!glossaryCount || glossaryCount <= limit) return null;
    if (limit === 0) return t('plans.loosingFeatures.glossaryUnusable');
    return t('plans.loosingFeatures.glossaryOverLimit', { limit });
  }


  // Funktion zur Höhenanpassung
  const adjustCardHeadHeights = () => {
    const cardHeads = document.querySelectorAll('.card-head');

    // reset first, the content may have changed (e.g. discounted prices after entering a code)
    cardHeads.forEach(cardHead => {
      cardHead.style.height = '';
    });

    let maxHeight = 0;

    // Finde die maximale Höhe
    cardHeads.forEach(cardHead => {
      const height = cardHead.offsetHeight;
      if (height > maxHeight) {
        maxHeight = height;
      }
    });

    // Setze die maximale Höhe für alle cardHead-Elemente
    cardHeads.forEach(cardHead => {
      cardHead.style.height = maxHeight + 'px';
    });
  };
  
  useEffect(() => {

    // Höhenanpassung nach dem Rendern
    adjustCardHeadHeights();

    // Optional: Hinzufügen eines Event-Listeners, um die Höhe bei einem Fenster-Resize anzupassen
    window.addEventListener('resize', adjustCardHeadHeights);

    // Cleanup-Funktion
    return () => {
      window.removeEventListener('resize', adjustCardHeadHeights);
    };
  }, []);

  // all entries of the shop in one database-only request. Capped at the current plan's limit, so it
  // only tells whether a lower limit is exceeded, never the real total
  useEffect(() => {
    if (shopPlan === 'free') return;
    API.getGlossaryExport(data => setGlossaryCount(Array.isArray(data?.entries) ? data.entries.length : null));
  }, [shopPlan]);

  useEffect(() => {
    if (featureList.length === 0) {
      dispatch(getPlans(() => {
        setLoaded(true);
        setTimeout(() => { 
          adjustCardHeadHeights();
        }, 100);
      }));
    } else {
      setLoaded(true);
    }

    setTimeout(() => {
      if (innerContainer?.current) {
        scrollContainer.current.style.overflow = 'hidden';
        scrollContainer.current.scroll({ left: (scrollContainer.current.clientWidth * 0.5) });
        setTimeout(() => {
          if (scrollContainer?.current?.style) {
            scrollContainer.current.style.overflow = 'auto';
          }
        }, 1000);
      }
    }, 0);
  }, [dispatch, featureList.length]);

  // discounted prices, trial and hints change the height of the card heads
  useEffect(() => {
    const timeout = setTimeout(adjustCardHeadHeights, 0);
    return () => clearTimeout(timeout);
  }, [plans]);

  const handleShowFullFeatureList = () => setShowFullList(true);
  const handleHideFullFeatureList = () => setShowFullList(false);
  const toggleAnualPayment = () => setAnualPayment(!anualPayment);

  const handleChangePlan = (newPlan) => {
    const newPlanIndex = _plans.findIndex(x => x === newPlan.toLowerCase());
    const oldPlanIndex = _plans.findIndex(x => x === shopPlan.toLowerCase());
    const isDowngrade = (newPlanIndex < oldPlanIndex);

    if (isDowngrade) {
      const newPlanKey = _plans[newPlanIndex];
      let newLoosingFeatures = [];
      for (let i = newPlanIndex + 1; i <= oldPlanIndex; i++) {
        newLoosingFeatures = [...newLoosingFeatures, ..._features[_plans[i]].map(key => describeLostFeature(key, newPlanKey))];
      }
      newLoosingFeatures.push(describeLostGlossary(newPlanKey));
      setLoosingFeatures(newLoosingFeatures.filter(Boolean));
      setShowDowngradeWarning(true);
      setNewPlanId(newPlan);
    } else {
      trackPlanChange('plan_upgrade', shopPlan, newPlan);
      setIsLoading(true);
      dispatch(changePlan(newPlan, handleChangePlanFailed));
    }
  }

  // Only called when the API did not redirect to the Shopify confirmation page (e.g. plan_not_allowed).
  // The discount error itself is shown by the DiscountCode overlay, which opens on it (state.plans.discount_error).
  const handleChangePlanFailed = () => {
    setIsLoading(false);
  }

  const handleDowngradeConfirmed = () => {
    trackPlanChange('plan_downgrade', shopPlan, newPlanId);
    setIsLoading(true);
    dispatch(changePlan(newPlanId, handleChangePlanFailed));
    setShowDowngradeWarning(false);
    setLoosingFeatures([]);
  }

  const handleChangeTab = (e, index) => setActiveTab(index);

  const _getButtonColor = (plan) => {
    if (plan.current) return 'yellow';
    if (plan.downgradable) return 'red';
    if (plan.upgradeable) return 'green';
    return 'default';
  }

  const renderLeftTableCard = () => {
    if (featureList.length === 0) return null;
    const planFeatures = showFullList ? featureList['Free'] : featureList['Free'].filter(x => x.tease);
    return (
      <Card sx={[psStyles.card, psStyles.fixedCard]} elevation={8}>
        <CardActionArea>
          <CardContent>
            <div className={'card-head'} style={{...psStyles.cardHead, marginBottom: 8}}>
              <CardMedia
                //component="img"
                sx={psStyles.media}
                image={`/files/images/price.webp`}
                onLoad={adjustCardHeadHeights()}
              />
              <div style={{height: 93}}>
                <Typography sx={psStyles.title} style={{ marginBottom: 22 }} color="primary" variant="h6" gutterBottom>
                  <Trans i18nKey={'plans.planSelect.forEveryone'}>PLANS FOR<br/>EVERYONE</Trans>
                </Typography>
              </div>

              {anualPayment && (
                <Typography sx={psStyles.discount} color="text.secondary" variant="h5" gutterBottom>
                  {t('plans.planSelect.discount', { discount: plan.anualDiscount })}
                </Typography>
              )}
              <Button sx={[psStyles.showAllFeatures, psStyles.button, psStyles.headButton, { alignSelf: 'center' }]} variant="contained" size="small" onClick={showFullList ? handleHideFullFeatureList : handleShowFullFeatureList}>
                {showFullList ? t('plans.planSelect.showLess') : t('plans.planSelect.showFullList')}
              </Button>
            </div>
            <TableContainer sx={psStyles.tableContainer} component="div">
              <Table sx={psStyles.table} aria-label={t('plans.planSelect.featureListTableAriaLabel')}>
                <TableBody>
                  {planFeatures.map((feature, index) => (
                    <TableRow
                      key={`feature-label-${feature.key}`}
                      sx={index % 2 === 0 ? psStyles.alternateRow : undefined}
                    >
                      <TableCell sx={psStyles.singleline} component="th" scope="row" size="small">
                        {t(`plans.features.${feature.key}`)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </CardActionArea>
      </Card>
    )
  }

  const renderCard = (plan, showFeatureLabel = false) => {
    if (!plan) return null;
    const planFeatures = showFullList ? featureList[plan.name] : featureList[plan.name].filter(x => x.tease);

    // discount: null | {code, allowed, price, base_price, discounted_price, lifetime_percent, free_forever, discount_type, discount_value, duration_months, trial_days}
    // base_price = list price after a lifetime cut (stays for good), discounted_price = base price after a limited discount
    const discount = plan.discount || null;
    const discountAllowed = !!discount && discount.allowed;
    const hasDiscountPrice = discountAllowed && parseFloat(discount.discounted_price) < parseFloat(discount.price);
    // lifetime cut the shop already holds from a used code: {percent, price, free_forever} | null
    const lifetime = plan.lifetime_discount || null;
    const lifetimePercent = discountAllowed && discount.lifetime_percent > 0 ? discount.lifetime_percent : (lifetime?.percent || 0);
    const freeForever = discountAllowed ? !!discount.free_forever : !!lifetime?.free_forever;
    // the backend lets the current paid plan be subscribed again to get the discount
    const canResubscribeWithDiscount = plan.current && discountAllowed && parseFloat(plan.price) > 0;
    const codeTrialDays = discountAllowed && discount.trial_days > 0 ? discount.trial_days : 0;
    // the discount (or trial) the current subscription already runs with (a pending code takes precedence)
    const runningDiscount = plan.current && !hasDiscountPrice && !codeTrialDays && currentDiscount
      && !(currentDiscount.duration_limit && currentDiscount.remaining === 0) ? currentDiscount : null;
    const hasRunningPrice = !!runningDiscount && !!runningDiscount.type && !anualPayment
      && runningDiscount.price !== null && runningDiscount.price_after_discount !== null
      && parseFloat(runningDiscount.price_after_discount) < parseFloat(runningDiscount.price);
    // the price that applies once a limited discount or a trial is over (a lifetime cut stays)
    const basePrice = discountAllowed && discount.base_price !== undefined ? discount.base_price : (lifetime ? lifetime.price : plan.price);
    const regularPrice = t('plans.planSelect.thenPrice', { price: formatPrice(basePrice) });

    let days = 0; let hours = 0; let minutes = 0;
    if(plan.downgradable && plan.downgradable.nextDowngrade) {
      const now = new Date();
      const future = new Date(plan.downgradable.nextDowngrade);
      const timeDiff = future - now;
      days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
      hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));      
    }

    return (
      <RedTooltip 
        disabled={!plan.downgradable?.nextDowngrade || (days <= 0 && hours <= 0 && minutes <= 0)} 
        title={<React.Fragment>
            <Typography>{t('plans.planSelect.nextDowngradePossibleIn')}</Typography>
            <Typography>{t('plans.planSelect.downgradeCountdown', { days, hours, minutes })}</Typography>
          </React.Fragment>
        }
        arrow
      >
        <Card className={classNames(plan.current && 'current', plan.name === 'Growth' && 'best-deal')} sx={psStyles.card}>
          <CardActionArea>
            <CardContent sx={psStyles.cardContent}>
              <div className={'card-head'} style={psStyles.cardHead}>
                <CardMedia
                  //component="img"
                  sx={psStyles.media}
                  image={`/files/images/${plan.name.toLowerCase()}.webp`}
                  title=""
                  onLoad={adjustCardHeadHeights}
                />
                <Typography sx={[psStyles.singleline, psStyles.title]} color="text.secondary" variant="h4" gutterBottom>
                  {plan.name}
                </Typography>
                {freeForever && parseFloat(plan.price) > 0 ? (
                  <Box className={'discounted-price'} data-testid={`plan-free-forever-${plan.key.toLowerCase()}`}>
                    <Typography sx={[psStyles.singleline, psStyles.price]} color="text.secondary" variant="body2" component="div">
                      <s title={t('plans.planSelect.listPrice')}>$ {parseFloat(plan.price).toFixed(2)}</s>
                    </Typography>
                    <Typography sx={[psStyles.singleline, psStyles.price]} color="success.main" variant="h6" component="div">
                      <strong>{t('plans.planSelect.freeForever')}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">{t('plans.planSelect.freeForeverHint')}</Typography>
                  </Box>
                ) : hasDiscountPrice ? (
                  <Box className={'discounted-price'} data-testid={`plan-discount-${plan.key.toLowerCase()}`}>
                    <Typography sx={[psStyles.singleline, psStyles.price]} color="text.secondary" variant="body2" component="div">
                      <s title={t('plans.planSelect.listPrice')}>$ {parseFloat(discount.price).toFixed(2)}</s>
                    </Typography>
                    <Typography sx={[psStyles.singleline, psStyles.price]} color="success.main" variant="h6" component="div">
                      <strong>$ {parseFloat(discount.discounted_price).toFixed(2)}</strong>
                      <small>
                        &nbsp;/ {!anualPayment ? t('plans.planSelect.monthly') : t('plans.planSelect.yearly')}
                      </small>
                    </Typography>
                    {discount.discount_type && (
                      <Typography variant="body2" color="text.secondary" data-testid={`plan-discount-duration-${plan.key.toLowerCase()}`}>
                        {discount.duration_months
                          ? `${t('plans.planSelect.discountForMonths', { count: discount.duration_months })}, ${regularPrice}`
                          : t('plans.planSelect.discountPermanent')}
                      </Typography>
                    )}
                    {lifetimePercent > 0 && (
                      <Typography variant="body2" color="success.main" data-testid={`plan-lifetime-${plan.key.toLowerCase()}`}>
                        {t('plans.planSelect.lifetimeCut', { percent: lifetimePercent })}
                      </Typography>
                    )}
                  </Box>
                ) : hasRunningPrice ? (
                  <Box className={'discounted-price'}>
                    <Typography sx={[psStyles.singleline, psStyles.price]} color="text.secondary" variant="body2" component="div">
                      <s title={t('plans.planSelect.listPrice')}>$ {parseFloat(runningDiscount.price).toFixed(2)}</s>
                    </Typography>
                    <Typography sx={[psStyles.singleline, psStyles.price]} color="success.main" variant="h6" component="div">
                      <strong>$ {parseFloat(runningDiscount.price_after_discount).toFixed(2)}</strong>
                      <small>
                        &nbsp;/ {t('plans.planSelect.monthly')}
                      </small>
                    </Typography>
                  </Box>
                ) : lifetime && !anualPayment && parseFloat(lifetime.price) < parseFloat(plan.price) ? (
                  <Box className={'discounted-price'} data-testid={`plan-lifetime-${plan.key.toLowerCase()}`}>
                    <Typography sx={[psStyles.singleline, psStyles.price]} color="text.secondary" variant="body2" component="div">
                      <s title={t('plans.planSelect.listPrice')}>$ {parseFloat(plan.price).toFixed(2)}</s>
                    </Typography>
                    <Typography sx={[psStyles.singleline, psStyles.price]} color="success.main" variant="h6" component="div">
                      <strong>$ {parseFloat(lifetime.price).toFixed(2)}</strong>
                      <small>&nbsp;/ {t('plans.planSelect.monthly')}</small>
                    </Typography>
                    <Typography variant="body2" color="success.main">{t('plans.planSelect.lifetimeCut', { percent: lifetime.percent })}</Typography>
                  </Box>
                ) : (
                  <Typography sx={[psStyles.singleline, psStyles.price]} color="text.secondary" variant="h6" gutterBottom>
                    <strong>$ {parseFloat(plan.price).toFixed(2)}</strong>
                    <small>
                      &nbsp;/ {!anualPayment ? t('plans.planSelect.monthly') : t('plans.planSelect.yearly')}
                    </small>
                  </Typography>
                )}
                {runningDiscount?.trial_ends_at && (
                  <Typography variant="body2" color="success.main" data-testid={`plan-current-trial-${plan.key.toLowerCase()}`}>
                    {t('plans.planSelect.trialUntil', { date: formatDate(runningDiscount.trial_ends_at) })}
                    {', '}
                    {t('plans.planSelect.thenPrice', { price: formatPrice(runningDiscount.type ? runningDiscount.price_after_discount : plan.price) })}
                  </Typography>
                )}
                {runningDiscount?.type && (
                  <Typography variant="body2" color="success.main" data-testid={`plan-current-discount-${plan.key.toLowerCase()}`}>
                    {runningDiscount.type === 'percentage'
                      ? t('plans.discountCode.benefitDiscountPercent', { value: runningDiscount.value })
                      : t('plans.discountCode.benefitDiscountAmount', { value: parseFloat(runningDiscount.value).toFixed(2) })}
                    {' '}
                    {!runningDiscount.duration_limit
                      ? t('plans.planSelect.discountPermanent')
                      : `${runningDiscount.remaining !== null && runningDiscount.remaining !== undefined
                        ? t('plans.planSelect.discountMonthsLeft', { count: runningDiscount.remaining })
                        : t('plans.planSelect.discountForMonths', { count: runningDiscount.duration_limit })}, ${regularPrice}`}
                  </Typography>
                )}
                {codeTrialDays > 0 && (
                  <Typography variant="body2" color="success.main" data-testid={`plan-trial-${plan.key.toLowerCase()}`}>
                    {trialLabel(t, codeTrialDays)}
                    {', '}
                    {t('plans.planSelect.thenPrice', { price: formatPrice(hasDiscountPrice ? discount.discounted_price : basePrice) })}
                  </Typography>
                )}
                {discountAllowed && pendingDiscount?.rules?.wordsOnActivation > 0 && (
                  <Typography variant="body2" color="success.main">
                    {t('plans.discountCode.benefitWords', { words: formatLargeNumber(pendingDiscount.rules.wordsOnActivation) })}
                  </Typography>
                )}
                {discount && !discount.allowed && (
                  <Typography variant="body2" color="error" data-testid={`plan-discount-not-allowed-${plan.key.toLowerCase()}`}>
                    {t('plans.planSelect.discountNotAllowed')}
                  </Typography>
                )}
                {anualPayment && (
                  <Typography sx={psStyles.discount} color="text.secondary" variant="h5" gutterBottom>
                    {plan.anualDiscount}%
                  </Typography>
                )}
                {plan.description &&
                  <Typography variant="subtitle1" alignContent="center" gutterBottom>
                    {plan.description}
                  </Typography>
                }
                <Button
                  variant={plan.current || plan.downgradable ? 'outlined' : 'contained'}
                  color="primary"
                  size={plan.key === shopPlan ? 'medium' : 'small'}
                  disabled={
                    (plan.current && !canResubscribeWithDiscount) ||
                    (plan.downgradable && !plan.downgradable.permitted) ||
                    (plan.upgradeable && !plan.upgradeable.permitted) ||
                    (!!discount && !discount.allowed)
                  }
                  onClick={canResubscribeWithDiscount
                    ? () => { setIsLoading(true); dispatch(changePlan(plan.key, handleChangePlanFailed)); }
                    : (plan.key !== shopPlan ? () => handleChangePlan(plan.key) : null)}
                  className={_getButtonColor(plan)} sx={[psStyles.button, psStyles.headButton, psStyles.singleline, plan.current && psStyles.currentPlanButton, !plan.current && !plan.downgradable && glintOnHover]}
                  fullWidth
                >
                  {canResubscribeWithDiscount ? t('plans.planSelect.applyDiscount') : plan.current ? t('plans.planSelect.currentPlan') : plan.downgradable ? t('plans.planSelect.downgradePlan') : t('plans.planSelect.upgradePlan')}
                </Button>
              </div>
              <TableContainer sx={psStyles.tableContainer} component="div">
                <Table className={'featureTable'} sx={psStyles.table} aria-label={t('plans.planSelect.planFeaturesTableAriaLabel')}>
                  <TableBody>
                    {planFeatures.map((feature, index) => (
                      <TableRow
                        key={`feature-${feature.key}`}
                        className={classNames('tease', !feature.tease && 'hide')}
                        sx={index % 2 === 0 ? psStyles.alternateRow : undefined}
                        style={{
                          fontWeight: feature.tease ? 'bold' : 'normal',
                        }}
                      >
                        {showFeatureLabel && (
                          <TableCell className={'featureLabel'} sx={feature.tease ? psStyles.bold : undefined} alignContent="start" component="th" scope="row" size="small">
                            <strong>{feature.name}</strong>
                          </TableCell>
                        )}
                        <TableCell className={!feature.tease ? 'hide' : undefined} sx={feature.tease ? psStyles.bold : undefined} component="th" scope="row" size="small">
                          <strong> </strong>
                            {typeof feature.value === 'string' && (feature.value === 'unlimited' ? t('plans.planSelect.unlimited') : feature.value)}
                            {typeof feature.value === 'number' && feature.value !== 0 && feature.value.toLocaleString()}
                            {typeof feature.value === 'boolean' && feature.value === true && <CheckIcon sx={psStyles.checkIcon} />}
                            {((typeof feature.value === 'boolean' && feature.value === false) || (typeof feature.value === 'number' && feature.value === 0))  && <CloseIcon sx={psStyles.noCheckIcon} />}
                         

                          {feature.key === 'words' && feature.value !== false && <small> {t('plans.planSelect.wordsUnit')}</small> }
                          {feature.key === 'onetime_words' && feature.value !== false && <small> {t('plans.planSelect.freeWordsUnit')}</small> }
                          {feature.key === 'max_word_discount' && feature.value !== 0 && <small> {t('plans.planSelect.percentUnit')}</small> }
                          {feature.key === 'available_words_log' && feature.value !== false && <small> {t('plans.planSelect.daysUnit')}</small> }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Button className={'showMoreBtn'} sx={psStyles.showMoreBtn} size="small" onClick={showFullList ? handleHideFullFeatureList : handleShowFullFeatureList}>
                  {showFullList ? t('plans.planSelect.showLess') : t('plans.planSelect.showFullList')}
                </Button>
              </TableContainer>
            </CardContent>
          </CardActionArea>
        </Card>        
      </RedTooltip>

    );
  }

  return (
    <Box sx={psStyles.root}>
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Box ref={scrollContainer} sx={psStyles.cardsContainer}>
          <div ref={innerContainer} className="innerContainer">
            {plans && plans.map((plan, index) => (
              <Box key={`plan-${index}`} className={index === 0 ? 'first' : undefined} sx={psStyles.gridItem}>
                {plan.name === 'Growth' &&
                  <div style={psStyles.bestDeal}>
                    {t('plans.planSelect.bestDeal')}
                  </div>
                }
                <div className="inner">
                  {renderCard(plan, false)}
                </div>
              </Box>
            ))}
          </div>
        </Box>
        {renderLeftTableCard()}
      </Box>
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <Tabs
          value={activeTab}
          onChange={handleChangeTab}
          indicatorColor="primary"
          textColor="primary"
          centered
          sx={psStyles.mobileTabs}
        >
          {plans.length > 0 && plans.map((plan, index) => (
            <Tab label={plan.name} value={index} key={`plan-mobile-${index}`} />
          ))}
        </Tabs>
        {renderCard(plans[activeTab], true)}
      </Box>
      {/* the page overlay: the old 25px spinner sat at 50% padding, off screen on a long page */}
      <LoadingIndicator pending={!loaded || isLoading} withLogo={false} />
      <Dialog open={showDowngradeWarning}>
        <DialogTitle>{t('plans.planSelect.downgradeWarningTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {t('plans.planSelect.downgradeWarningMessage')}
          </Typography>
          <Box component="ul" sx={psStyles.lostFeatureList}>
            {loosingFeatures.map((feature, index) => (
              <Typography variant="body1" key={index} component={'li'} sx={psStyles.lostFeature}>
                <CloseIcon sx={psStyles.lostFeatureIcon} />
                {feature}
              </Typography>
            ))}
          </Box>
          {newPlanId?.toLowerCase() === 'free' &&
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t('plans.planSelect.downgradeFreeBilling')}
            </Typography>
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDowngradeWarning(false)} variant="outlined">
            {t('plans.planSelect.cancelButton')}
          </Button>
          <Button onClick={handleDowngradeConfirmed} variant="contained" sx={psStyles.downgradeButton}>
            {t('plans.planSelect.confirmDowngradeButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}